import type { ChartSpec } from "./types";

export type ChartTimeRange = {
  startMs: number;
  endMs: number;
  spanMs: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function chartTimeMilliseconds(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const normalizeNumber = (candidate: number): number | null => {
    if (!Number.isFinite(candidate)) return null;
    if (Math.abs(candidate) > 100_000_000_000_000_000) return candidate / 1_000_000;
    if (Math.abs(candidate) > 100_000_000_000_000) return candidate / 1_000;
    if (Math.abs(candidate) > 100_000_000_000) return candidate;
    return candidate * 1000;
  };
  if (typeof value === 'number') return normalizeNumber(value);
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (value.trim() !== '' && Number.isFinite(numeric)) return normalizeNumber(numeric);
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = new Date(String(value)).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function dateFormatter(
  options: Intl.DateTimeFormatOptions,
  timezone?: string,
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      timeZone: timezone || "UTC",
    });
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      timeZone: "UTC",
    });
  }
}

export function chartTimeRange(spec: ChartSpec): ChartTimeRange | null {
  if (spec.xAxis?.type !== "time" || !Array.isArray(spec.data)) return null;
  const timestamps = spec.data
    .map((row) => chartTimeMilliseconds(row[spec.xAxis.key]))
    .filter((value): value is number => value !== null)
    .sort((left, right) => left - right);
  if (timestamps.length === 0) return null;
  const startMs = timestamps[0];
  const endMs = timestamps[timestamps.length - 1];
  return { startMs, endMs, spanMs: Math.max(0, endMs - startMs) };
}

export function formatChartTime(
  value: unknown,
  range: ChartTimeRange | null,
  timezone?: string,
  detail = false,
): string {
  const milliseconds = chartTimeMilliseconds(value);
  if (milliseconds === null) return String(value ?? "");
  const date = new Date(milliseconds);
  if (!Number.isFinite(date.getTime())) return String(value ?? "");
  if (detail) {
    return dateFormatter(
      {
        year: "numeric",
        month: "short",
        day: "numeric",
        ...(range && range.spanMs <= 2 * DAY_MS
          ? { hour: "numeric", minute: "2-digit" }
          : {}),
      },
      timezone,
    ).format(date);
  }
  if (range && range.spanMs >= 365 * DAY_MS) {
    return dateFormatter(
      { year: "numeric", month: "short" },
      timezone,
    ).format(date);
  }
  if (range && range.spanMs >= 45 * DAY_MS) {
    return dateFormatter(
      { year: "2-digit", month: "short", day: "numeric" },
      timezone,
    ).format(date);
  }
  if (range && range.spanMs <= 2 * DAY_MS) {
    return dateFormatter(
      { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
      timezone,
    ).format(date);
  }
  return dateFormatter({ month: "short", day: "numeric" }, timezone).format(
    date,
  );
}

export function formatChartTimeRange(
  range: ChartTimeRange,
  timezone?: string,
): string {
  const formatter = dateFormatter(
    { year: "numeric", month: "short", day: "numeric" },
    timezone,
  );
  return `${formatter.format(new Date(range.startMs))} to ${formatter.format(
    new Date(range.endMs),
  )}`;
}
